import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { MENU, EXPENSES_BASE, ACCESS_ID } from "./config";
import { useExpensesAccess, useExpensesAccessLocked } from "./hooks/useExpensesAccess";
import { useCategoryCatalog } from "./hooks/useCategoryCatalog";
import { useMenuEntitlement } from "../../hooks/useEntitlement";

// Shell de la app "Gastos y Presupuestos" (#615). Renderiza el sub-menú plano
// de secciones (derivado de MENU), el apps-sidebar y la sección activa vía
// <Outlet/>. Espeja WorkHoursLayout, con una puerta más: solo entra quien está
// en la lista de acceso; al resto se le explica, sin llamar a la API de gastos.
export default function ExpensesLayout() {
  // Ítems de menú por derecho (`menu:expenses.<id>`, #803).
  const canSeeMenuItem = useMenuEntitlement("expenses");
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  // Un administrador de reseller no pertenece a ninguna empresa: elige cuál
  // administrar desde el selector. Sin esa elección, las secciones llamarían a
  // la API sin empresa, el servidor respondería 403 y el interceptor global
  // cerraría la sesión de toda la app. Mejor decirlo y no llamar.
  const hasCompany = Boolean(
    localStorage.getItem("companyId") || localStorage.getItem("userCompanyId"),
  );

  const access = useExpensesAccess(true, { fresh: true });
  const accessLocked = useExpensesAccessLocked();
  // Categorías de la compañía (#1255): nombres, colores y opciones de todas las secciones.
  const catalog = useCategoryCatalog(access === "allowed" && hasCompany);

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Sub-menús abiertos. «Flujo» arranca abierto: sus hijos son parte del recorrido.
  const [expandedMenus, setExpandedMenus] = useState<string[]>(() => MENU.filter((item) => item.children?.length).map((item) => item.id));

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  // «Acceso» solo sirve donde la lista se puede editar: con la lista de dueños
  // fija en código (CC-CONSOLE) no se pinta. La ruta sigue existiendo.
  const menuItems = MENU.filter((item) => canSeeMenuItem(item.id) && !(item.id === ACCESS_ID && accessLocked)).map((item) => {
    const path = `${EXPENSES_BASE}/${item.id}`;
    const children = item.children
      ?.filter((child) => canSeeMenuItem(child.id))
      .map((child) => ({ id: child.id, label: t(child.labelKey), icon: child.icon, path: `${EXPENSES_BASE}/${child.id}` }));
    // La página del padre como primer sub-ítem (Presupuestos → Dashboard). Sin
    // otros hijos a la vista no hace falta: el ítem queda como uno normal.
    const self = item.self && children?.length ? [{ id: item.id, label: t(item.self.labelKey), icon: item.self.icon, path }] : [];
    return { id: item.id, label: t(item.labelKey), icon: item.icon, path, children: children && [...self, ...children] };
  });
  // El sub-ítem que repite la página del padre no es otra sección.
  const sectionCount = menuItems.reduce((n, item) => n + 1 + (item.children?.filter((child) => child.id !== item.id).length || 0), 0);
  /** Padres cuya página ya está resaltada como sub-ítem: abiertos, no se resaltan dos veces. */
  const showsSelf = (itemId: string) =>
    expandedMenus.includes(itemId) && Boolean(menuItems.find((item) => item.id === itemId)?.children?.some((child) => child.id === itemId));

  // Un ítem con hijos también es una página (la portada de Flujo, el Dashboard
  // de Presupuestos): el primer clic la abre y deja los hijos a la vista;
  // estando en ella, pliega o despliega.
  const toggleSubmenu = (menuId: string) => {
    const path = `${EXPENSES_BASE}/${menuId}`;
    if (location.pathname !== path) {
      navigate(path);
      setExpandedMenus((prev) => (prev.includes(menuId) ? prev : [...prev, menuId]));
      return;
    }
    setExpandedMenus((prev) => (prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]));
  };

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="flex h-screen pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("expenses.appName")}
        subtitle={t("expenses.appSubtitle")}
        onOpenAppsSidebar={openAppsSidebar}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname === `${EXPENSES_BASE}/${itemId}` && !showsSelf(itemId)}
          expandedMenus={expandedMenus}
          onToggleSubmenu={toggleSubmenu}
          footerStats={{
            label: t("expenses.appName"),
            items: [{ label: t("expenses.sections"), value: sectionCount }],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          {access !== "allowed" ? (
            <div className="flex flex-col items-center justify-center text-center gap-2 p-10" data-testid="no-access">
              {access === "unknown" ? (
                <i className="ri-loader-4-line animate-spin text-3xl text-teal-600"></i>
              ) : (
                <>
                  <i className={`ri-lock-2-line text-3xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
                  <p className={`text-lg font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                    {t(access === "error" ? "expenses.noAccess.errorTitle" : "expenses.noAccess.title")}
                  </p>
                  <p className={`text-sm max-w-md ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t(access === "error" ? "expenses.noAccess.errorText" : "expenses.noAccess.text")}
                  </p>
                </>
              )}
            </div>
          ) : hasCompany ? (
            catalog.ready ? (
              <Outlet key={catalog.version} />
            ) : (
              <div className="flex items-center justify-center p-10" data-testid="catalog-loading">
                <i className="ri-loader-4-line animate-spin text-3xl text-teal-600"></i>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center text-center gap-2 p-10" data-testid="no-company">
              <i className={`ri-building-line text-3xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
              <p className={`text-lg font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                {t("expenses.noCompany.title")}
              </p>
              <p className={`text-sm max-w-md ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("expenses.noCompany.text")}
              </p>
            </div>
          )}
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="expenses"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
